import { BaseScreenProps } from '@/features/quiz/types'
import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { AnswerChoiceListItem } from '@/features/quiz/answer-choice-list-item'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCloudArrowUp,
  faInfoCircle,
  faSquareCheck,
} from '@fortawesome/pro-solid-svg-icons'
import { faImage, faPlus, faXmark } from '@fortawesome/pro-regular-svg-icons'
import BottomContinueButton from '@/features/quiz/bottom-continue-button'
import QuizQuestionContainer from '@/features/quiz/quiz-question-container'
import ScreenTitle from '@/features/quiz/screen-title'
import { object, string } from 'yup'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import QuizTextareaInput from '@/features/quiz/inputs/quiz-textarea-input'
import { useDebounce } from '@uidotdev/usehooks'
import {
  BooleanQuestionFormat,
  FileUploadAnswer,
  FileUploadQuestionFormat,
  MultipleChoiceQuestionFormat,
  PhotoUploadAnswer,
  PhotoUploadQuestionFormat,
  QuestionSelectionError,
  QuizQuestionFormat,
  SingleChoiceGroupedQuestionFormat,
  SingleChoiceQuestionFormat,
  TextQuestionFormat,
  TextWithCheckboxQuestionFormat,
  UploadedPhoto,
  UploadedQuizFile,
} from '@/lib/apis/types'
import QuizSelectionOption from '@/features/quiz/inputs/quiz-selection-option'
import Image from 'next/image'
import { Image as MappedImage, ImageMap } from '@/shared/image-map'
import {
  GetQuizFilePreviewUrl,
  GetQuizPhotoPreviewUrl,
  UploadQuizFiles,
  UploadQuizPhotos,
} from '@/lib/apis/patients'
import useApiClient from '@/lib/apis/clients/useApiClient'
import { useMutation, useQueries } from '@tanstack/react-query'
import FileInputButton from '@/shared/components/buttons/FileInputButton'
import NcMarkdown from '@/shared/components/NcMarkdown'
import { DialogModal } from '@/shared/components/DialogModal'
import {
  NotificationItem,
  NotificationItemVariant,
} from '@/lib/models/NotificationItem'
import { useStore } from '@/lib/store'
import Spinner from '@/shared/components/Spinner'
import { faFile, faFilePdf } from '@fortawesome/pro-duotone-svg-icons'
import { faShieldCheck } from '@fortawesome/pro-solid-svg-icons'
import idVerificationBgImage from '@/images/elements/id-verification-bg.png'
import fullBodyImage from '@/images/elements/full-body-bg.png'
import idVerificationFemaleBgImage from '@/images/elements/id-verification-female-bg.png'
import fullBodyFemaleImage from '@/images/elements/full-body-female-bg.png'
import {
  ASSIGNED_SEX_AT_BIRTH_FEMALE,
  ASSIGNED_SEX_AT_BIRTH_KEY,
} from '@/features/quiz/constants'

type Props = {
  format: QuizQuestionFormat
} & BaseScreenProps

// For single question quiz screens, the question ID is the same as screen ID.
export default function SingleQuestionQuizScreen({
  format,

  ...props
}: Props) {
  switch (format.type) {
    case 'single-choice':
      return <SingleChoiceScreen {...props} format={format} />
    case 'single-choice-grouped':
      return <SingleChoiceGroupedScreen {...props} format={format} />
    case 'multiple-choice':
      return <MultipleChoiceScreen {...props} format={format} />
    case 'boolean-question':
      return <BooleanScreen {...props} format={format} />
    case 'text':
      return <TextScreen {...props} format={format} />
    case 'text-with-checkbox':
      return <TextWithCheckboxScreen {...props} format={format} />
    case 'photo-upload':
      return <PhotoUploadScreen {...props} format={format} />
    case 'file-upload':
      return <FileUploadScreen {...props} format={format} />
    default:
      return null
  }
}

type SingleChoiceGroupedScreenProps = Omit<Props, 'format'> & {
  format: SingleChoiceGroupedQuestionFormat
}

const iconSizes = {
  '3x2': {
    width: 144,
    height: 96,
  },
} satisfies Record<MappedImage['ratio'], { width: number; height: number }>

type QuestionSelectionOption = SingleChoiceQuestionFormat['choices'][number]
type SelectionIconPosition = NonNullable<
  NonNullable<QuestionSelectionOption['icon']>['position']
>

function SingleChoiceOptionContent({
  answer,
}: {
  answer: QuestionSelectionOption
}) {
  const image = answer.icon
    ? ImageMap[answer.key as keyof typeof ImageMap]
    : undefined

  if (!image) {
    return (
      <AnswerChoiceListItem title={answer.title} subtitle={answer.subtitle} />
    )
  }

  const iconSize = iconSizes[image.ratio]
  const iconPosition: SelectionIconPosition = answer.icon?.position ?? 'Right'

  const icon = (
    <Image
      src={image.data}
      alt=""
      width={iconSize.width}
      height={iconSize.height}
      className="h-auto w-20 shrink-0 object-contain sm:w-24"
      aria-hidden={true}
    />
  )

  return (
    <div
      className={clsx(
        'flex w-full items-center gap-4',
        iconPosition === 'Right' ? 'flex-row' : 'flex-row-reverse',
      )}
    >
      <div className="min-w-0 flex-1">
        <AnswerChoiceListItem title={answer.title} subtitle={answer.subtitle} />
      </div>
      {icon}
    </div>
  )
}

function SingleChoiceGroupedScreen({
  screenId,
  format,
  answers,
  answered,
  onChange,
  onError,
}: SingleChoiceGroupedScreenProps) {
  const answersForCurrentScreen = answers[screenId]

  if (
    answersForCurrentScreen &&
    answersForCurrentScreen.type !== 'single-choice'
  ) {
    throw new Error('Unexpected answer type')
  }

  const [selectedAnswer, setSelectedAnswer] = useState(
    answersForCurrentScreen?.answer ?? null,
  )

  const { title, subtitle, choices } = format

  return (
    <QuizQuestionContainer header={{ title, subtitle }}>
      <div className="space-y-6 sm:space-y-7 lg:space-y-8">
        {choices.map((group, idx) => (
          <div key={idx} className="space-y-3 sm:space-y-4 lg:space-y-5">
            <ScreenTitle
              title={group.title}
              subtitle={group.subtitle ?? undefined}
            />

            {group.choices.map((answer) => (
              <QuizSelectionOption
                key={answer.key}
                selected={selectedAnswer === answer.key}
                onClick={async () => {
                  setSelectedAnswer(answer.key)

                  if (answer.errorIfSelected) {
                    onError(answer.errorIfSelected as QuestionSelectionError)
                    return
                  }

                  await onChange(
                    screenId,
                    {
                      id: screenId,
                      type: 'single-choice',
                      answer: answer.key,
                    },
                    !answered,
                  )
                }}
              >
                <SingleChoiceOptionContent answer={answer} />
              </QuizSelectionOption>
            ))}
          </div>
        ))}
      </div>
    </QuizQuestionContainer>
  )
}

type SingleChoiceScreenProps = Omit<Props, 'format'> & {
  format: SingleChoiceQuestionFormat
}

function SingleChoiceScreen({
  screenId,
  format,
  answers,
  answered,
  onChange,
  onError,
}: SingleChoiceScreenProps) {
  const answersForCurrentScreen = answers[screenId]

  if (
    answersForCurrentScreen &&
    answersForCurrentScreen.type !== 'single-choice'
  ) {
    throw new Error('Unexpected answer type')
  }

  const [selectedAnswer, setSelectedAnswer] = useState(
    answersForCurrentScreen?.answer ?? null,
  )

  const { title, subtitle, choices } = format

  return (
    <QuizQuestionContainer header={{ title, subtitle }}>
      {choices.map((answer) => (
        <QuizSelectionOption
          key={answer.key}
          selected={selectedAnswer === answer.key}
          onClick={async () => {
            setSelectedAnswer(answer.key)

            if (answer.errorIfSelected) {
              onError(answer.errorIfSelected as QuestionSelectionError)
              return
            }

            await onChange(
              screenId,
              {
                id: screenId,
                type: 'single-choice',
                answer: answer.key,
              },
              !answered,
            )
          }}
          popular={answer.popular === true}
        >
          <SingleChoiceOptionContent answer={answer} />
        </QuizSelectionOption>
      ))}
    </QuizQuestionContainer>
  )
}

type BooleanScreenProps = Omit<Props, 'format'> & {
  format: BooleanQuestionFormat
}

function BooleanScreen({
  screenId,
  format,
  answers,
  answered,
  onChange,
  onError,
}: BooleanScreenProps) {
  const answersForCurrentScreen = answers[screenId]

  if (answersForCurrentScreen && answersForCurrentScreen.type !== 'boolean') {
    throw new Error('Unexpected answer type')
  }

  const [selectedAnswer, setSelectedAnswer] = useState(
    answersForCurrentScreen?.answer ?? undefined,
  )

  const choices = [
    ...(format.yes
      ? [
          {
            value: true,
            key: 'true',
            errorIfSelected: undefined,
            title: format.yes.title,
            subtitle: format.yes.subtitle,
          },
        ]
      : []),
    ...(format.no
      ? [
          {
            value: false,
            key: 'false',
            errorIfSelected: undefined,
            title: format.no.title,
            subtitle: format.no.subtitle,
          },
        ]
      : []),
  ]

  const { title, subtitle } = format

  return (
    <QuizQuestionContainer header={{ title, subtitle }}>
      <div className="space-y-6 sm:space-y-7 lg:space-y-8">
        {choices.map((answer) => (
          <QuizSelectionOption
            key={answer.key}
            selected={selectedAnswer === answer.value}
            onClick={async () => {
              setSelectedAnswer(answer.value)

              if (answer.errorIfSelected) {
                onError(answer.errorIfSelected)
                return
              }

              await onChange(
                screenId,
                {
                  id: screenId,
                  type: 'boolean',
                  answer: answer.value,
                },
                !answered,
              )
            }}
          >
            <AnswerChoiceListItem
              title={answer.title}
              subtitle={answer.subtitle}
            />
          </QuizSelectionOption>
        ))}
      </div>
    </QuizQuestionContainer>
  )
}

type PhotoUploadScreenProps = Omit<Props, 'format'> & {
  format: PhotoUploadQuestionFormat
}

type FileUploadScreenProps = Omit<Props, 'format'> & {
  format: FileUploadQuestionFormat
}

type UploadingAsset = {
  id: string
  name: string
  type: string
  previewUrl: string
  uploadedId?: string
  uploadState:
    | {
        uploading: true
        progress: number
      }
    | {
        uploading: false
      }
}

type UploadSelection = {
  url: string
  name: string
  mimeType: string
}

const MAX_UPLOAD_SIZE = 10 * 1024 * 1024

function isImageMimeType(type: string) {
  return type.startsWith('image/')
}

function isPdfMimeType(type: string) {
  return (
    type === 'application/pdf' || type.endsWith('/pdf') || type.endsWith('pdf')
  )
}

function toUploadedPhotos(photos: UploadingAsset[]): UploadedPhoto[] {
  return photos
    .filter(
      (photo): photo is UploadingAsset & { uploadedId: string } =>
        !photo.uploadState.uploading &&
        typeof photo.uploadedId === 'string' &&
        photo.uploadedId.length > 0,
    )
    .map((photo) => ({
      id: photo.uploadedId,
      name: photo.name,
      mimeType: photo.type,
    }))
}

function toPhotoUploadAnswer(
  screenId: string,
  photos: UploadingAsset[],
): PhotoUploadAnswer {
  return {
    id: screenId,
    type: 'photo-upload',
    photos: toUploadedPhotos(photos),
  }
}

function toUploadedFiles(files: UploadingAsset[]): UploadedQuizFile[] {
  return files
    .filter(
      (file): file is UploadingAsset & { uploadedId: string } =>
        !file.uploadState.uploading &&
        typeof file.uploadedId === 'string' &&
        file.uploadedId.length > 0,
    )
    .map((file) => ({
      id: file.uploadedId,
      name: file.name,
      mimeType: file.type,
    }))
}

function toFileUploadAnswer(
  screenId: string,
  files: UploadingAsset[],
): FileUploadAnswer {
  return {
    id: screenId,
    type: 'file-upload',
    files: toUploadedFiles(files),
  }
}

function PhotoUploadScreen({
  screenId,
  format,
  answers,
  onChange,
  treatmentType,
}: PhotoUploadScreenProps) {
  const answerForCurrentScreen = answers[screenId]
  if (
    answerForCurrentScreen &&
    answerForCurrentScreen.type !== 'photo-upload'
  ) {
    throw new Error('Unexpected answer type')
  }

  const api = useApiClient()
  const uploadQuizPhotoMutation = useMutation({
    ...UploadQuizPhotos.mutation(api),
    onSuccess: (result, variables) => {
      const { document } = variables
      const previewUrl = document.url

      if (!result.id || result.id.trim().length === 0) {
        setPhotos((previousPhotos) => {
          const failedPhoto = previousPhotos.find(
            (photo) => photo.previewUrl === previewUrl,
          )
          if (failedPhoto?.previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(failedPhoto.previewUrl)
          }

          return previousPhotos.filter(
            (photo) => photo.previewUrl !== previewUrl,
          )
        })
        showNotification(
          new NotificationItem(
            `Failed to process ${document.name}. Please try again.`,
            NotificationItemVariant.Error,
          ),
        )
        return
      }

      setPhotos((previousPhotos) => {
        const updatedPhotos = previousPhotos.map((photo) => {
          if (photo.previewUrl !== previewUrl) return photo

          return {
            ...photo,
            uploadedId: result.id,
            uploadState: {
              uploading: false as const,
            },
          }
        })

        persistDraft(updatedPhotos)
        return updatedPhotos
      })
    },
    onError: (_, variables) => {
      const { document } = variables
      const previewUrl = document.url

      setPhotos((previousPhotos) => {
        const photoToRemove = previousPhotos.find(
          (photo) => photo.previewUrl === previewUrl,
        )
        if (photoToRemove?.previewUrl.startsWith('blob:'))
          URL.revokeObjectURL(photoToRemove.previewUrl)

        return previousPhotos.filter((photo) => photo.previewUrl !== previewUrl)
      })

      showNotification(
        new NotificationItem(
          `Failed to upload ${document.name}. Please try again.`,
          NotificationItemVariant.Error,
        ),
      )
    },
  })
  const showNotification = useStore((state) => state.showNotification)

  const [photos, setPhotos] = useState<UploadingAsset[]>(
    answerForCurrentScreen?.photos.map((photo) => ({
      id: `${photo.id ?? photo.name}`,
      name: photo.name,
      type: photo.mimeType,
      previewUrl: '',
      uploadedId: photo.id && photo.id.trim().length > 0 ? photo.id : undefined,
      uploadState: {
        uploading: false as const,
      },
    })) ?? [],
  )

  const previewQueries = useQueries({
    queries: photos.map((photo) => ({
      queryKey: ['quiz-photo-preview', photo.uploadedId ?? '', photo.id],
      enabled: !photo.uploadState.uploading && !!photo.uploadedId,
      staleTime: 10 * 60 * 1000,
      queryFn: async () => {
        if (!photo.uploadedId) return ''
        const preview = await GetQuizPhotoPreviewUrl.api(api, {
          id: photo.uploadedId,
        })
        return preview.url
      },
    })),
  })
  const [loadedPreviewImages, setLoadedPreviewImages] = useState<
    Record<string, true>
  >({})
  const [failedPreviewImages, setFailedPreviewImages] = useState<
    Record<string, true>
  >({})
  const [selectedPreview, setSelectedPreview] =
    useState<UploadSelection | null>(null)

  function markPreviewImageLoaded(previewKey?: string) {
    if (!previewKey) return

    setLoadedPreviewImages((current) => {
      if (current[previewKey]) return current
      return { ...current, [previewKey]: true as const }
    })
  }

  function markPreviewImageFailed(previewKey?: string) {
    if (!previewKey) return

    setFailedPreviewImages((current) => {
      if (current[previewKey]) return current
      return { ...current, [previewKey]: true as const }
    })
  }

  const photosAreUploading = photos.some((photo) => photo.uploadState.uploading)
  const uploadedPhotos = toUploadedPhotos(photos)
  const allowMultiple = format.allowMultiple !== false
  const canAddMorePhotos = allowMultiple || photos.length === 0
  const canContinue = !photosAreUploading && uploadedPhotos.length > 0
  const renderId = format.renderId
  const assignedSexAtBirthAnswer = answers[ASSIGNED_SEX_AT_BIRTH_KEY]
  const useFemaleArtwork =
    treatmentType === 'wl' &&
    assignedSexAtBirthAnswer?.type === 'single-choice' &&
    assignedSexAtBirthAnswer.answer === ASSIGNED_SEX_AT_BIRTH_FEMALE
  const showMobilePhotoActions =
    treatmentType === 'wl' &&
    (renderId === 'IDVerification' || renderId === 'FullBodyPhoto')
  const showEntirePhotoPreview =
    renderId === 'IDVerification' || renderId === 'FullBodyPhoto'

  function persistDraft(updatedPhotos: UploadingAsset[]) {
    onChange(screenId, toPhotoUploadAnswer(screenId, updatedPhotos), false)
      .then()
      .catch(console.error)
  }

  function removePhoto(photoId: string) {
    setPhotos((previousPhotos) => {
      const removedPhoto = previousPhotos.find((photo) => photo.id === photoId)
      if (removedPhoto?.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(removedPhoto.previewUrl)
      }

      const updatedPhotos = previousPhotos.filter(
        (photo) => photo.id !== photoId,
      )
      persistDraft(updatedPhotos)
      return updatedPhotos
    })
  }

  function queuePhotoForUpload(file: File) {
    if (!isImageMimeType(file.type)) {
      showNotification(
        new NotificationItem(
          `Unsupported file type for ${file.name}. Please upload an image.`,
          NotificationItemVariant.Error,
        ),
      )
      return
    }

    if (file.size > MAX_UPLOAD_SIZE) {
      showNotification(
        new NotificationItem(
          `${file.name} is larger than 10MB.`,
          NotificationItemVariant.Error,
        ),
      )
      return
    }

    const previewUrl = URL.createObjectURL(file)
    const photoId = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2)}`
    const initialPhoto: UploadingAsset = {
      id: photoId,
      name: file.name,
      type: file.type,
      previewUrl,
      uploadState: {
        uploading: true,
        progress: 0,
      },
    }

    setPhotos((previousPhotos) => [...previousPhotos, initialPhoto])

    uploadQuizPhotoMutation.mutate({
      document: {
        name: file.name,
        type: file.type,
        url: previewUrl,
      },
      updateProgress: (progress) =>
        setPhotos((previousPhotos) =>
          previousPhotos.map((photo) => {
            if (photo.id !== photoId || !photo.uploadState.uploading) {
              return photo
            }

            return {
              ...photo,
              uploadState: {
                uploading: true as const,
                progress,
              },
            }
          }),
        ),
    })
  }

  function onSelectPhotos(event: React.ChangeEvent<HTMLInputElement>) {
    const { files } = event.target
    if (!files || files.length === 0) return

    const selected = Array.from(files)
    if (!allowMultiple && photos.length > 0) {
      showNotification(
        new NotificationItem(
          'Only one photo can be uploaded for this question.',
          NotificationItemVariant.Error,
        ),
      )
      event.target.value = ''
      return
    }

    const acceptedFiles = allowMultiple ? selected : selected.slice(0, 1)
    if (!allowMultiple && selected.length > 1) {
      showNotification(
        new NotificationItem(
          'Only the first selected photo was queued for upload.',
          NotificationItemVariant.Warning,
        ),
      )
    }

    for (const file of acceptedFiles) {
      queuePhotoForUpload(file)
    }

    event.target.value = ''
  }

  return (
    <QuizQuestionContainer
      header={{ title: format.title, subtitle: format.subtitle }}
    >
      {format.notice && (
        <div className="rounded-lg bg-gray-100 px-4 py-3 ring-1 ring-gray-200">
          <div className="flex items-center gap-x-2">
            <p>
              <FontAwesomeIcon icon={faInfoCircle} className="text-gray-900" />
            </p>
            <NcMarkdown
              className="text-sm leading-6 text-gray-900"
              target="_blank"
            >
              {format.notice}
            </NcMarkdown>
          </div>
        </div>
      )}

      {photos.length === 0 ? (
        renderUploadButton()
      ) : (
        <ul
          className={clsx(
            'grid gap-3',
            allowMultiple ? 'grid-cols-2' : 'grid-cols-1',
          )}
        >
          {photos.map((photo, idx) => {
            const previewQuery = previewQueries[idx]
            const previewUrl =
              !photo.uploadState.uploading && previewQuery?.data
                ? previewQuery.data
                : photo.previewUrl
            const previewQueryIsLoading =
              !photo.uploadState.uploading &&
              !!photo.uploadedId &&
              (previewQuery?.isLoading || previewQuery?.isFetching)
            const previewKey = previewUrl ? `${photo.id}:${previewUrl}` : ''
            const previewFailed = previewKey
              ? !!failedPreviewImages[previewKey]
              : false
            const canRenderPreview = !!previewUrl && !previewFailed
            const previewImageIsLoading = previewKey
              ? !loadedPreviewImages[previewKey]
              : false
            const showOverlaySpinner =
              photo.uploadState.uploading ||
              previewQueryIsLoading ||
              (canRenderPreview && previewImageIsLoading)

            return (
              <li
                key={photo.id}
                className="relative overflow-hidden rounded-xl border border-gray-200 bg-white"
              >
                <button
                  className="absolute right-2 top-2 z-20 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-gray-700 shadow-sm transition-colors hover:bg-white hover:text-gray-900"
                  onClick={() => removePhoto(photo.id)}
                  type="button"
                  aria-label={`Remove ${photo.name}`}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>

                {canRenderPreview ? (
                  <button
                    type="button"
                    className="relative block aspect-square w-full"
                    onClick={() =>
                      setSelectedPreview({
                        url: previewUrl,
                        name: photo.name,
                        mimeType: photo.type,
                      })
                    }
                    aria-label={`Preview ${photo.name}`}
                  >
                    <Image
                      src={previewUrl}
                      alt={photo.name}
                      fill
                      unoptimized
                      className={clsx(
                        'transition-opacity',
                        showEntirePhotoPreview
                          ? 'object-contain'
                          : 'object-cover',
                        showOverlaySpinner ? 'opacity-70' : 'opacity-100',
                      )}
                      onLoad={() => markPreviewImageLoaded(previewKey)}
                      onError={() => markPreviewImageFailed(previewKey)}
                    />
                  </button>
                ) : (
                  <div className="flex aspect-square w-full items-center justify-center bg-gray-100 text-gray-500">
                    <FontAwesomeIcon icon={faImage} size="6x" />
                  </div>
                )}
                {showOverlaySpinner && (
                  <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-gray-900/20">
                    <Spinner size={6} circleColor="white" />
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {photos.length > 0 && allowMultiple && (
        <div className="flex w-full items-center justify-center gap-3">
          <FileInputButton
            id={`${screenId}-photo-library`}
            accept="image/*"
            multiple={allowMultiple}
            disabled={!canAddMorePhotos}
            onChange={(event) => onSelectPhotos(event)}
            className="flex items-center justify-center !rounded-md border border-gray-950 bg-white px-3.5 py-2 text-sm font-semibold !text-gray-950 transition-all enabled:hover:bg-gray-100 disabled:bg-gray-200 disabled:text-gray-400"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add another
          </FileInputButton>
        </div>
      )}

      <BottomContinueButton
        show={true}
        initial={false}
        disabled={!canContinue}
        onClick={async () => {
          if (photosAreUploading) return

          await onChange(screenId, toPhotoUploadAnswer(screenId, photos), true)
        }}
        buttonProps={{
          loading: photosAreUploading,
          children: photosAreUploading ? 'Uploading...' : 'Continue',
        }}
      />

      <DialogModal
        show={selectedPreview !== null}
        onClose={() => setSelectedPreview(null)}
        width="4xl"
        panelClasses="!p-0 !overflow-visible"
        panelContainerClasses="!p-0"
      >
        {selectedPreview && (
          <div className="relative">
            <button
              type="button"
              className="absolute right-0 top-0 z-[200] -translate-y-1/3 translate-x-1/3 rounded-full bg-white p-2 text-black"
              onClick={() => setSelectedPreview(null)}
            >
              <FontAwesomeIcon icon={faXmark} size="lg" />
            </button>
            <div className="relative mx-auto aspect-[4/3] w-full max-w-none overflow-hidden rounded-lg bg-black">
              <Image
                src={selectedPreview.url}
                alt={selectedPreview.name}
                fill
                unoptimized
                className="object-contain"
              />
            </div>
          </div>
        )}
      </DialogModal>
    </QuizQuestionContainer>
  )

  function renderUploadButton() {
    let bgImage = null
    let topText = null

    switch (renderId) {
      case 'IDVerification':
        bgImage = useFemaleArtwork
          ? idVerificationFemaleBgImage
          : idVerificationBgImage
        topText = (
          <p className="flex items-center gap-x-1 px-3 py-2 font-sans text-sm font-medium text-steel-navy">
            <FontAwesomeIcon icon={faShieldCheck} /> Passport or Australian
            driver{"'"}s license
          </p>
        )
        break
      case 'FullBodyPhoto':
        bgImage = useFemaleArtwork ? fullBodyFemaleImage : fullBodyImage
    }

    const borderClasses = 'border border-dashed border-gray-300'

    return (
      <div className="flex flex-col gap-y-2.5">
        {bgImage ? (
          <>
            <div
              className={clsx(
                'aspect-[16/9] overflow-hidden rounded-lg',
                borderClasses,
              )}
            >
              {topText}
              <Image
                src={bgImage}
                alt=""
                className="h-auto w-full object-cover"
              />
            </div>

            {showMobilePhotoActions && (
              <div className="flex flex-col gap-2.5 sm:hidden">
                <FileInputButton
                  id={`${screenId}-photo-camera`}
                  accept="image/*"
                  capture={
                    renderId === 'IDVerification' ? 'user' : 'environment'
                  }
                  disabled={!canAddMorePhotos}
                  onChange={(event) => onSelectPhotos(event)}
                  containerClassName="flex"
                  className="flex-1 whitespace-nowrap !rounded-lg !bg-steel-navy px-3 py-3 text-center text-clinical-white hover:!bg-rich-black"
                >
                  Take a photo
                </FileInputButton>
                <FileInputButton
                  id={`${screenId}-photo-library-mobile`}
                  accept="image/*"
                  multiple={allowMultiple}
                  disabled={!canAddMorePhotos}
                  onChange={(event) => onSelectPhotos(event)}
                  containerClassName="flex"
                  className="flex-1 whitespace-nowrap !rounded-lg border border-steel-navy !bg-clinical-white px-3 py-3 text-center !text-steel-navy hover:!bg-gray-100"
                >
                  Upload a photo
                </FileInputButton>
              </div>
            )}

            <FileInputButton
              id={`${screenId}-photo-library`}
              accept="image/*"
              multiple={allowMultiple}
              disabled={!canAddMorePhotos}
              onChange={(event) => onSelectPhotos(event)}
              containerClassName={clsx(
                'flex',
                showMobilePhotoActions && 'hidden sm:flex',
              )}
              className="flex-1 !rounded-lg bg-gray-950 px-6 py-3 text-center text-clinical-white hover:bg-gray-800"
            >
              Choose File
            </FileInputButton>
          </>
        ) : (
          <FileInputButton
            id={`${screenId}-photo-library`}
            accept="image/*"
            multiple={allowMultiple}
            disabled={!canAddMorePhotos}
            onChange={(event) => onSelectPhotos(event)}
            containerClassName={clsx(
              'rounded-xl bg-gray-100 px-5 py-8 text-center text-gray-600',
              borderClasses,
            )}
            className="bg-transparent text-transparent"
          >
            <div className="flex flex-col items-center gap-y-3.5 text-gray-500">
              <div className="flex flex-col gap-y-0.5">
                <p>
                  <FontAwesomeIcon icon={faCloudArrowUp} size="3x" />
                </p>
                <p className="text-base font-normal text-gray-500">
                  {allowMultiple
                    ? 'Upload at least one photo to continue.'
                    : 'Upload photo to continue.'}
                </p>
              </div>
              <p className="rounded-lg bg-gray-950 px-6 py-2 text-sm font-semibold text-clinical-white transition-all hover:bg-gray-800">
                Choose File
              </p>
            </div>
          </FileInputButton>
        )}
      </div>
    )
  }
}

function FileUploadScreen({
  screenId,
  format,
  answers,
  onChange,
}: FileUploadScreenProps) {
  const answerForCurrentScreen = answers[screenId]
  if (answerForCurrentScreen && answerForCurrentScreen.type !== 'file-upload') {
    throw new Error('Unexpected answer type')
  }

  const api = useApiClient()
  const showNotification = useStore((state) => state.showNotification)
  const uploadQuizFileMutation = useMutation({
    ...UploadQuizFiles.mutation(api),
    onSuccess: (result, variables) => {
      const { document } = variables
      const previewUrl = document.url

      if (!result.id || result.id.trim().length === 0) {
        setFiles((previousFiles) => {
          const failedFile = previousFiles.find(
            (file) => file.previewUrl === previewUrl,
          )
          if (failedFile?.previewUrl.startsWith('blob:')) {
            URL.revokeObjectURL(failedFile.previewUrl)
          }

          return previousFiles.filter((file) => file.previewUrl !== previewUrl)
        })
        showNotification(
          new NotificationItem(
            `Failed to process ${document.name}. Please try again.`,
            NotificationItemVariant.Error,
          ),
        )
        return
      }

      setFiles((previousFiles) => {
        const updatedFiles = previousFiles.map((file) => {
          if (file.previewUrl !== previewUrl) return file

          return {
            ...file,
            uploadedId: result.id,
            uploadState: {
              uploading: false as const,
            },
          }
        })

        persistDraft(updatedFiles)
        return updatedFiles
      })
    },
    onError: (_, variables) => {
      const { document } = variables
      const previewUrl = document.url

      setFiles((previousFiles) => {
        const fileToRemove = previousFiles.find(
          (file) => file.previewUrl === previewUrl,
        )
        if (fileToRemove?.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(fileToRemove.previewUrl)
        }

        return previousFiles.filter((file) => file.previewUrl !== previewUrl)
      })

      showNotification(
        new NotificationItem(
          `Failed to upload ${document.name}. Please try again.`,
          NotificationItemVariant.Error,
        ),
      )
    },
  })

  const [files, setFiles] = useState<UploadingAsset[]>(
    answerForCurrentScreen?.files.map((file) => ({
      id: `${file.id ?? file.name}`,
      name: file.name,
      type: file.mimeType,
      previewUrl: '',
      uploadedId: file.id && file.id.trim().length > 0 ? file.id : undefined,
      uploadState: {
        uploading: false as const,
      },
    })) ?? [],
  )

  const previewQueries = useQueries({
    queries: files.map((file) => ({
      queryKey: ['quiz-file-preview', file.uploadedId ?? '', file.id],
      enabled: !file.uploadState.uploading && !!file.uploadedId,
      staleTime: 10 * 60 * 1000,
      queryFn: async () => {
        if (!file.uploadedId) return ''
        const preview = await GetQuizFilePreviewUrl.api(api, {
          id: file.uploadedId,
        })
        return preview.url
      },
    })),
  })
  const [loadedPreviewImages, setLoadedPreviewImages] = useState<
    Record<string, true>
  >({})
  const [failedPreviewImages, setFailedPreviewImages] = useState<
    Record<string, true>
  >({})
  const [selectedPreview, setSelectedPreview] =
    useState<UploadSelection | null>(null)

  function markPreviewImageLoaded(previewKey?: string) {
    if (!previewKey) return

    setLoadedPreviewImages((current) => {
      if (current[previewKey]) return current
      return { ...current, [previewKey]: true as const }
    })
  }

  function markPreviewImageFailed(previewKey?: string) {
    if (!previewKey) return

    setFailedPreviewImages((current) => {
      if (current[previewKey]) return current
      return { ...current, [previewKey]: true as const }
    })
  }

  const filesAreUploading = files.some((file) => file.uploadState.uploading)
  const uploadedFiles = toUploadedFiles(files)
  const allowMultiple = format.allowMultiple !== false
  const canAddMoreFiles = allowMultiple || files.length === 0
  const canContinue = !filesAreUploading && uploadedFiles.length > 0

  function persistDraft(updatedFiles: UploadingAsset[]) {
    onChange(screenId, toFileUploadAnswer(screenId, updatedFiles), false)
      .then()
      .catch(console.error)
  }

  function removeFile(fileId: string) {
    setFiles((previousFiles) => {
      const removedFile = previousFiles.find((file) => file.id === fileId)
      if (removedFile?.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(removedFile.previewUrl)
      }

      const updatedFiles = previousFiles.filter((file) => file.id !== fileId)
      persistDraft(updatedFiles)
      return updatedFiles
    })
  }

  function queueFileForUpload(file: File) {
    if (!isImageMimeType(file.type) && !isPdfMimeType(file.type)) {
      showNotification(
        new NotificationItem(
          `Unsupported file type for ${file.name}. Please upload a PDF or image.`,
          NotificationItemVariant.Error,
        ),
      )
      return
    }

    if (file.size > MAX_UPLOAD_SIZE) {
      showNotification(
        new NotificationItem(
          `${file.name} is larger than 10MB.`,
          NotificationItemVariant.Error,
        ),
      )
      return
    }

    const previewUrl = URL.createObjectURL(file)
    const fileId = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2)}`
    const initialFile: UploadingAsset = {
      id: fileId,
      name: file.name,
      type: file.type,
      previewUrl,
      uploadState: {
        uploading: true,
        progress: 0,
      },
    }

    setFiles((previousFiles) => [...previousFiles, initialFile])

    uploadQuizFileMutation.mutate({
      document: {
        name: file.name,
        type: file.type,
        url: previewUrl,
      },
      updateProgress: (progress) =>
        setFiles((previousFiles) =>
          previousFiles.map((existingFile) => {
            if (
              existingFile.id !== fileId ||
              !existingFile.uploadState.uploading
            ) {
              return existingFile
            }

            return {
              ...existingFile,
              uploadState: {
                uploading: true as const,
                progress,
              },
            }
          }),
        ),
    })
  }

  function onSelectFiles(event: React.ChangeEvent<HTMLInputElement>) {
    const inputFiles = event.target.files
    if (!inputFiles || inputFiles.length === 0) return

    const selected = Array.from(inputFiles)
    if (!allowMultiple && files.length > 0) {
      showNotification(
        new NotificationItem(
          'Only one file can be uploaded for this question.',
          NotificationItemVariant.Error,
        ),
      )
      event.target.value = ''
      return
    }

    const acceptedFiles = allowMultiple ? selected : selected.slice(0, 1)
    if (!allowMultiple && selected.length > 1) {
      showNotification(
        new NotificationItem(
          'Only the first selected file was queued for upload.',
          NotificationItemVariant.Warning,
        ),
      )
    }

    for (const file of acceptedFiles) {
      queueFileForUpload(file)
    }

    event.target.value = ''
  }

  return (
    <QuizQuestionContainer
      header={{ title: format.title, subtitle: format.subtitle }}
    >
      {format.notice && (
        <div className="rounded-lg bg-gray-100 px-4 py-3 ring-1 ring-gray-200">
          <div className="flex items-center gap-x-2">
            <p>
              <FontAwesomeIcon icon={faInfoCircle} className="text-gray-900" />
            </p>
            <NcMarkdown
              className="text-sm leading-6 text-gray-900"
              target="_blank"
            >
              {format.notice}
            </NcMarkdown>
          </div>
        </div>
      )}

      {files.length === 0 ? (
        <FileInputButton
          id={`${screenId}-file-library`}
          accept="image/*,.pdf,application/pdf"
          multiple={allowMultiple}
          disabled={!canAddMoreFiles}
          onChange={(event) => onSelectFiles(event)}
          containerClassName="rounded-xl border border-dashed border-gray-300 bg-gray-100 px-5 py-8 text-center text-gray-600"
          className="bg-transparent text-transparent"
        >
          <div className="flex flex-col items-center gap-y-3.5 text-gray-500">
            <div className="flex flex-col gap-y-0.5">
              <p>
                <FontAwesomeIcon icon={faCloudArrowUp} size="3x" />
              </p>
              <p className="text-base font-normal text-gray-500">
                Upload at least one file to continue.
              </p>
            </div>
            <p className="rounded-lg bg-gray-950 px-6 py-2 text-sm font-semibold text-clinical-white transition-all hover:bg-gray-800">
              Choose File
            </p>
          </div>
        </FileInputButton>
      ) : (
        <ul className="grid grid-cols-4 gap-3">
          {files.map((file, idx) => {
            const previewQuery = previewQueries[idx]
            const previewUrl =
              !file.uploadState.uploading && previewQuery?.data
                ? previewQuery.data
                : file.previewUrl
            const previewQueryIsLoading =
              !file.uploadState.uploading &&
              !!file.uploadedId &&
              (previewQuery?.isLoading || previewQuery?.isFetching)
            const isImage = isImageMimeType(file.type)
            const previewKey =
              previewUrl && isImage ? `${file.id}:${previewUrl}` : ''
            const previewFailed = previewKey
              ? !!failedPreviewImages[previewKey]
              : false
            const canRenderPreview = !!previewUrl && isImage && !previewFailed
            const previewImageIsLoading = previewKey
              ? !loadedPreviewImages[previewKey]
              : false
            const showOverlaySpinner =
              file.uploadState.uploading ||
              previewQueryIsLoading ||
              (canRenderPreview && previewImageIsLoading)
            const fileIcon = isPdfMimeType(file.type) ? faFilePdf : faFile

            return (
              <li
                key={file.id}
                className="relative overflow-hidden rounded-xl border border-gray-200 bg-white"
              >
                <button
                  className="absolute right-2 top-2 z-20 flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-gray-700 shadow-sm transition-colors hover:bg-white hover:text-gray-900"
                  onClick={() => removeFile(file.id)}
                  type="button"
                  aria-label={`Remove ${file.name}`}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>

                {canRenderPreview ? (
                  <button
                    type="button"
                    className="relative block aspect-square w-full"
                    onClick={() =>
                      setSelectedPreview({
                        url: previewUrl,
                        name: file.name,
                        mimeType: file.type,
                      })
                    }
                    aria-label={`Preview ${file.name}`}
                  >
                    <Image
                      src={previewUrl}
                      alt={file.name}
                      fill
                      unoptimized
                      className={clsx(
                        'object-cover transition-opacity',
                        showOverlaySpinner ? 'opacity-70' : 'opacity-100',
                      )}
                      onLoad={() => markPreviewImageLoaded(previewKey)}
                      onError={() => markPreviewImageFailed(previewKey)}
                    />
                  </button>
                ) : previewUrl ? (
                  <button
                    type="button"
                    className="flex aspect-square w-full flex-col items-center justify-center gap-2 bg-gray-100 px-3 text-center text-gray-600"
                    onClick={() =>
                      setSelectedPreview({
                        url: previewUrl,
                        name: file.name,
                        mimeType: file.type,
                      })
                    }
                    aria-label={`Preview ${file.name}`}
                  >
                    <FontAwesomeIcon icon={fileIcon} size="2x" />
                    <span className="line-clamp-2 text-xs font-medium">
                      {file.name}
                    </span>
                  </button>
                ) : (
                  <div className="flex aspect-square w-full flex-col items-center justify-center gap-2 bg-gray-100 px-3 text-center text-gray-500">
                    <FontAwesomeIcon icon={fileIcon} size="3x" />
                    <span className="line-clamp-2 text-xs font-medium">
                      {file.name}
                    </span>
                  </div>
                )}
                {showOverlaySpinner && (
                  <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-gray-900/20">
                    <Spinner size={6} circleColor="white" />
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {files.length > 0 && allowMultiple && (
        <div className="flex w-full items-center justify-center gap-3">
          <FileInputButton
            id={`${screenId}-file-library`}
            accept="image/*,.pdf,application/pdf"
            multiple={allowMultiple}
            disabled={!canAddMoreFiles}
            onChange={(event) => onSelectFiles(event)}
            className="flex items-center justify-center !rounded-md border border-gray-950 bg-white px-3.5 py-2 text-sm font-semibold !text-gray-950 transition-all enabled:hover:bg-gray-100 disabled:bg-gray-200 disabled:text-gray-400"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add another
          </FileInputButton>
        </div>
      )}

      <BottomContinueButton
        show={true}
        initial={false}
        disabled={!canContinue}
        onClick={async () => {
          if (filesAreUploading) return

          await onChange(screenId, toFileUploadAnswer(screenId, files), true)
        }}
        buttonProps={{
          loading: filesAreUploading,
          children: filesAreUploading ? 'Uploading...' : 'Continue',
        }}
      />

      <DialogModal
        show={selectedPreview !== null}
        onClose={() => setSelectedPreview(null)}
        width="4xl"
        panelClasses="!p-0 !overflow-visible"
        panelContainerClasses="!p-0"
      >
        {selectedPreview &&
          (isImageMimeType(selectedPreview.mimeType) ? (
            <div className="relative">
              <button
                type="button"
                className="absolute right-0 top-0 z-[200] -translate-y-1/3 translate-x-1/3 rounded-full bg-white p-2 text-black"
                onClick={() => setSelectedPreview(null)}
              >
                <FontAwesomeIcon icon={faXmark} size="lg" />
              </button>
              <div className="relative mx-auto aspect-[4/3] w-full max-w-none overflow-hidden rounded-lg bg-black">
                <Image
                  src={selectedPreview.url}
                  alt={selectedPreview.name}
                  fill
                  unoptimized
                  className="object-contain"
                />
              </div>
            </div>
          ) : (
            <div className="relative rounded-lg bg-white p-4">
              <button
                type="button"
                className="absolute right-2 top-2 z-[200] rounded-full bg-white p-2 text-black"
                onClick={() => setSelectedPreview(null)}
              >
                <FontAwesomeIcon icon={faXmark} size="lg" />
              </button>
              <iframe
                src={selectedPreview.url}
                title={selectedPreview.name}
                className="h-[80vh] w-full rounded-lg"
              />
            </div>
          ))}
      </DialogModal>
    </QuizQuestionContainer>
  )
}

type MultipleChoiceScreenProps = Omit<Props, 'format'> & {
  format: MultipleChoiceQuestionFormat
}

function MultipleChoiceScreen({
  screenId,
  format,
  answers,
  onNext,
  onChange,
}: MultipleChoiceScreenProps) {
  const answersForCurrentScreen = answers[screenId]
  if (
    answersForCurrentScreen &&
    answersForCurrentScreen.type !== 'multiple-choice'
  ) {
    throw new Error('Unexpected answer type')
  }

  // This set is used to compute whether the user has
  const [initialAnswers] = useState(
    new Set(answersForCurrentScreen?.answers ?? []),
  )

  const [selectedAnswers, setSelectedAnswers] = useState(
    answersForCurrentScreen?.answers ?? [],
  )

  const { title, subtitle, choices } = format
  const keysOfOnlyOneAllowed = choices
    .filter((choice) => choice.onlyOneAllowed === true)
    .map((choice) => choice.key)

  return (
    <QuizQuestionContainer header={{ title, subtitle }}>
      <>
        {choices.map((answer) => {
          const answerIsSelected = selectedAnswers.indexOf(answer.key) !== -1

          // TODO: Refactor, this probably should be done easily with
          //   `QuizSelectionOption`
          return (
            <div
              key={answer.key}
              className={clsx(
                'before:content[""] relative flex cursor-pointer items-center justify-between gap-x-6 rounded-xl px-5 py-6 transition-all duration-150 before:absolute before:left-0 before:top-0 before:z-[1] before:h-full before:w-full before:rounded-xl before:transition-all lg:py-7',
                'cursor-pointer bg-white hover:before:border-gray-400',
                answerIsSelected
                  ? 'before:border-2 before:border-blue-800 hover:before:!border-blue-800'
                  : 'before:border before:border-gray-300',
              )}
              onClick={async (e) => {
                let updatedAnswers: Array<string> = []

                if (answer.onlyOneAllowed) {
                  // If the "All of the above" option is already selected,
                  // selecting it again will make it disappear.
                  if (!answerIsSelected) updatedAnswers = [answer.key]
                } else {
                  let onlyOneAllowedRemoved = [...selectedAnswers]

                  // Remove the choice like "All of the above" from the answer
                  // since something else must have been selected
                  if (keysOfOnlyOneAllowed.length > 0) {
                    onlyOneAllowedRemoved = onlyOneAllowedRemoved.filter(
                      (key) => keysOfOnlyOneAllowed.indexOf(key) === -1,
                    )
                  }

                  if (answerIsSelected) {
                    updatedAnswers = [...onlyOneAllowedRemoved].filter(
                      (ans) => ans !== answer.key,
                    )
                  } else {
                    updatedAnswers = [...onlyOneAllowedRemoved, answer.key]
                  }
                }

                setSelectedAnswers(updatedAnswers)

                await onChange(
                  screenId,
                  {
                    id: screenId,
                    type: 'multiple-choice',
                    answers: updatedAnswers,
                  },
                  false,
                )
              }}
            >
              <AnswerChoiceListItem
                title={answer.title}
                subtitle={answer.subtitle}
              />
              <span className="block">
                <FontAwesomeIcon
                  icon={faSquareCheck}
                  className={clsx(
                    answerIsSelected ? 'text-blue-800' : 'text-gray-300',
                  )}
                  size="lg"
                />
              </span>
            </div>
          )
        })}
        <div>
          <BottomContinueButton
            onClick={async () => {
              const initialSelection = new Set(initialAnswers)
              let changed = false
              for (const answer of selectedAnswers) {
                if (!initialSelection.has(answer)) {
                  changed = true
                  break
                }

                initialSelection.delete(answer)
              }

              changed = changed || initialSelection.size > 0
              if (changed) {
                await onChange(
                  screenId,
                  {
                    id: screenId,
                    type: 'multiple-choice',
                    answers: selectedAnswers,
                  },
                  true,
                )
              } else {
                onNext()
              }
            }}
            initial={false}
            show={selectedAnswers.length > 0}
            disabled={selectedAnswers.length === 0}
          />
        </div>
      </>
    </QuizQuestionContainer>
  )
}

type TextScreenProps = Omit<Props, 'format'> & {
  format: TextQuestionFormat

  values?: any
  disabled?: boolean
}

function TextScreen({
  screenId,
  format,
  answers,
  onChange,
  values,
  disabled,
  children,
}: PropsWithChildren<TextScreenProps>) {
  const answerForCurrentScreen = answers[screenId]
  if (answerForCurrentScreen && answerForCurrentScreen.type !== 'text') {
    throw new Error('Unexpected answer type')
  }

  const schema = useMemo(() => {
    if (format.optional === true)
      return object({
        value: string(),
      })

    return object({
      value: string().required(
        format.errorMessageIfEmpty ??
          'Please tell us more about your condition.',
      ),
    })
  }, [format.optional, format.errorMessageIfEmpty])

  let actualValues = values
  if (actualValues === undefined) {
    actualValues = answerForCurrentScreen?.answer ?? ''
  }

  const methods = useForm({
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    mode: 'onTouched',
    defaultValues: {
      value: '',
    },
    values: {
      value: actualValues,
    },
    resolver: yupResolver(schema),
  })

  const { formState, handleSubmit, watch } = methods
  const { isValid, isReady } = formState
  const { value } = watch()
  const debouncedValue = useDebounce(value, 300)
  const { title, subtitle } = format

  // Sometimes when the user updates and click back, their progress might be lost
  // if we don't save the value
  useEffect(() => {
    // TODO: This might be a temporary fix. Currently when the user clicks next
    //   and back, the debounced value will temporarily show up as blank.
    //   This will update the existing typed value with blank which might revert
    //   what the user typed. Checking for `isReady` seems to have fixed this
    //   issue although i don't really understand why yet.
    if (!isReady) return

    // Only save edits, so opening a text screen does not save a blank answer.
    if ((value ?? '') === (answerForCurrentScreen?.answer ?? '')) return

    onChange(
      screenId,
      {
        id: screenId,
        type: 'text',
        answer: value ?? '',
      },
      false,
    )
      .then()
      .catch(console.error)
    // Don't care about any other changes, only the `value`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, debouncedValue])

  async function onSubmit(values: { value?: string }) {
    await onChange(
      screenId,
      {
        id: screenId,
        type: 'text',
        answer: values.value ?? '',
      },
      true,
    )
  }

  return (
    <QuizQuestionContainer header={{ title, subtitle }}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <QuizTextareaInput
            name="value"
            label={format.label ?? 'Tell us more'}
            disabled={disabled}
            rows={6}
          />
          {children}
          <div>
            <BottomContinueButton
              initial={false}
              show={isValid}
              disabled={!isValid}
              buttonProps={{
                type: 'submit',
              }}
            />
          </div>
        </form>
      </FormProvider>
    </QuizQuestionContainer>
  )
}

type TextWithCheckboxScreenProps = Omit<Props, 'format'> & {
  format: TextWithCheckboxQuestionFormat
}

function TextWithCheckboxScreen(props: TextWithCheckboxScreenProps) {
  const { screenId, answers, format, onBatchChange } = props
  const checkedId = `${screenId}-checked`
  const answerIsChecked = answers[checkedId]

  if (answerIsChecked && answerIsChecked.type !== 'boolean') {
    throw new Error('Unexpected answer type')
  }

  const [isChecked, setIsChecked] = useState(answerIsChecked?.answer ?? false)
  const values = isChecked ? format.checkboxText : undefined

  function onCheck() {
    if (!isChecked) {
      setIsChecked(true)
      onBatchChange(
        [
          [
            screenId,
            { id: screenId, type: 'text', answer: format.checkboxText },
          ],
          [checkedId, { id: checkedId, type: 'boolean', answer: true }],
        ],
        false,
      )
    } else {
      setIsChecked(false)
      onBatchChange(
        [[checkedId, { id: checkedId, type: 'boolean', answer: false }]],
        false,
      )
    }
  }

  return (
    <TextScreen
      {...props}
      format={{ ...format, type: 'text' }}
      values={values}
      disabled={isChecked}
    >
      <div className="mt-4 flex items-center justify-start gap-x-3">
        <input
          onChange={(e) => onCheck()}
          type="checkbox"
          id={checkedId}
          name={checkedId}
          checked={isChecked}
          className={clsx(
            'h-5 w-5 rounded-md border-gray-300 focus:ring-0 active:ring-0',
          )}
        />

        <label
          htmlFor={checkedId}
          className={clsx('block text-base leading-6 text-gray-900')}
        >
          {format.checkboxText}
        </label>
      </div>
    </TextScreen>
  )
}
